import React, {useEffect, useId, useState} from "react"
import {FormProvider, useWatch} from "react-hook-form"
import {useTranslation} from "react-i18next"
import {MinusIcon, PlusIcon} from "@heroicons/react/20/solid"
import {isAxiosError} from "axios"

import {Alert, GenericErrorAlert} from "../../../components/Alert.tsx"
import Button from "../../../components/Button.tsx"
import {Loading} from "../../../components/Loading.tsx"
import Modal from "../../../components/Modal.tsx"
import {useKlaraCompanySettingsMutation, useKlaraCompanySettingsQuery} from "../../../queries/klaraDashboard.ts"
import {useUserSettingsOrLogout} from "../../../queries/user.ts"
import {AKlaraValidationErrors} from "../../../services/types.generated.ts"
import {isSaaSCompanyUser} from "../../../utils/types.ts"
import {isAxiosValidationError} from "../../../utils/validation.ts"
import {
  SalesPersonSettingsFields,
  useSalesPersonSettingsForm,
} from "../../SalesAssistant/components/SalesPersonSettingsModal.tsx"
import {useKlaraSummary} from "../useDashboardClock.ts"

export const COVERAGE_TARGET_MIN = 10
export const COVERAGE_TARGET_MAX = 100
export const COVERAGE_TARGET_STEP = 5

/** The nearest coverage target the backend accepts: a multiple of the step within the allowed range. */
export const clampCoverageTarget = (value: number): number =>
  Math.min(
    COVERAGE_TARGET_MAX,
    Math.max(COVERAGE_TARGET_MIN, Math.round(value / COVERAGE_TARGET_STEP) * COVERAGE_TARGET_STEP)
  )

// The dashboard's own shell for the shared modal: 640px wide, 16px corners, 32/36px padding and a 28px title.
const SETTINGS_SHELL_CLASS_NAME =
  "pixel-units max-w-[640px] rounded-2xl sm:px-9 sm:pt-8 sm:pb-7 [&>h3]:mb-2 [&>h3]:border-b [&>h3]:border-cr-blue-light [&>h3]:pb-5 [&>h3]:text-[28px] [&>h3]:leading-tight [&>h3]:font-bold [&>h3]:tracking-[-0.5px]"

export const KlaraSettingsModal: React.FC<{isOpen: boolean; onClose: () => void}> = ({isOpen, onClose}) => {
  const {t} = useTranslation()
  const {user} = useUserSettingsOrLogout()

  // Personal settings belong to a sales person; the settings query must only run for a linked user.
  const isLinked = isSaaSCompanyUser(user) && user.sales_assistant_sales_person != null

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className={SETTINGS_SHELL_CLASS_NAME}
      title={t("KlaraDashboard_Settings_Title")}
    >
      {isLinked ? <LinkedSettingsForm onSaved={onClose} /> : <SettingsForm onSaved={onClose} />}
    </Modal>
  )
}

type TPersonalSettingsForm = ReturnType<typeof useSalesPersonSettingsForm>

const LinkedSettingsForm: React.FC<{onSaved: () => void}> = ({onSaved}) => {
  const personal = useSalesPersonSettingsForm({keepDraftsOnRefetch: true})

  return <SettingsForm onSaved={onSaved} personal={personal} />
}

type TSavedBlocks = {personal: boolean; company: boolean}

/** Saves only the changed blocks, in parallel; closes when every one of them succeeded. */
const SettingsForm: React.FC<{onSaved: () => void; personal?: TPersonalSettingsForm}> = ({onSaved, personal}) => {
  const {t} = useTranslation()
  const company = useCoverageTargetDraft()
  const [isSaving, setIsSaving] = useState(false)
  // Blocks an earlier attempt saved; each shows as saved only while it still equals what was saved.
  const [savedBlocks, setSavedBlocks] = useState<TSavedBlocks>({personal: false, company: false})
  const [hasPersonalSaveError, setHasPersonalSaveError] = useState(false)
  const [companyError, setCompanyError] = useState<string | null>(null)

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setHasPersonalSaveError(false)
    setCompanyError(null)
    // Enter submits without leaving the target input, so its draft is normalized here too.
    company.commitDraft()

    const personalChanged = personal?.hasChanges() ?? false
    const companyTarget = company.changedTarget()

    // An invalid personal draft blocks the whole save, so nothing is sent.
    if (personal && personalChanged && !(await personal.form.trigger(undefined, {shouldFocus: true}))) {
      return
    }

    setIsSaving(true)
    const [personalResult, companyResult] = await Promise.allSettled([
      personal && personalChanged ? personal.submit() : Promise.resolve("saved" as const),
      companyTarget != null ? company.save(companyTarget) : Promise.resolve(),
    ])
    setIsSaving(false)

    const personalSaved = personalResult.status === "fulfilled" && personalResult.value === "saved"
    const companySaved = companyResult.status === "fulfilled"

    if (personalSaved && companySaved) {
      onSaved()
      return
    }

    setSavedBlocks(previous => ({
      personal: personalChanged ? personalSaved : previous.personal,
      company: companyTarget != null ? companySaved : previous.company,
    }))
    // API validation errors are already on the personal fields; anything else gets the generic alert.
    if (personalResult.status === "rejected" && !isAxiosValidationError(personalResult.reason)) {
      setHasPersonalSaveError(true)
    }
    if (companyResult.status === "rejected") {
      setCompanyError(companySaveErrorMessage(companyResult.reason) ?? t("KlaraDashboard_Settings_SaveError"))
    }
  }

  const form = (
    // The blocks validate in the app: the target input's native range and step checks would block Enter.
    <form noValidate onSubmit={handleSubmit} className={"pixel-units"}>
      {/* Edits made while saving would not be part of the save, so the blocks are frozen until it settles. */}
      <fieldset disabled={isSaving} className={"min-w-0"}>
        {personal && (
          <PersonalSettingsBlock
            personal={personal}
            wasSaved={savedBlocks.personal}
            hasSaveError={hasPersonalSaveError}
            isSaving={isSaving}
          />
        )}

        <CompanySettingsBlock company={company} wasSaved={savedBlocks.company} error={companyError} />
      </fieldset>

      <div className={"mt-4 mb-6 h-px w-full border-t border-cr-blue-light"} />

      <div className={"flex justify-end"}>
        <Button variant={"contained"} color={"blue"} type={"submit"} isLoading={isSaving}>
          {t("KlaraDashboard_Settings_SaveChanges")}
        </Button>
      </div>
    </form>
  )

  return personal ? <FormProvider {...personal.form}>{form}</FormProvider> : form
}

const STEPPER_BUTTON_CLASS_NAME =
  "flex h-11 w-10 items-center justify-center bg-cr-white text-cr-slate-60 transition-colors hover:bg-cr-blue-super-light hover:text-cr-blue disabled:cursor-not-allowed disabled:text-cr-grey-15 disabled:hover:bg-cr-white"

const companySaveErrorMessage = (error: unknown): string | undefined => {
  if (!isAxiosError<Partial<AKlaraValidationErrors>>(error)) {
    return undefined
  }
  const errors = error.response?.data?.errors
  return errors?.coverage_target?.[0] ?? errors?.base?.[0]
}

const BlockHeading: React.FC<{title: string; isSaved: boolean}> = ({title, isSaved}) => {
  const {t} = useTranslation()

  return (
    <div className={"mb-4 flex items-center gap-2"}>
      <h3 className={"text-base font-bold text-cr-grey-90"}>{title}</h3>
      {isSaved && (
        <span role={"status"} className={"text-xs font-semibold text-cr-green-dark"}>
          {t("KlaraDashboard_Settings_Saved")}
        </span>
      )}
    </div>
  )
}

const PersonalSettingsBlock: React.FC<{
  personal: TPersonalSettingsForm
  wasSaved: boolean
  hasSaveError: boolean
  isSaving: boolean
}> = ({personal, wasSaved, hasSaveError, isSaving}) => {
  const {t} = useTranslation()
  const {isLoading, error, refetch, form, needsReload, reloadSaved} = personal
  // Re-renders on every edit, so the saved marker follows the values.
  useWatch({control: form.control})
  // Errors the API reports for the whole record (`base`) have no field to show on.
  const rootErrorMessage = form.formState.errors.root?.message

  return (
    <section className={"mb-6"}>
      <BlockHeading title={t("KlaraDashboard_Settings_Personal_Title")} isSaved={wasSaved && !personal.hasChanges()} />
      {needsReload ? (
        <GenericErrorAlert retry={reloadSaved} />
      ) : error ? (
        <GenericErrorAlert retry={refetch} />
      ) : isLoading ? (
        <Loading size={"xl"} />
      ) : (
        <>
          {hasSaveError ? (
            <Alert variant={"error"} title={t("KlaraDashboard_Settings_SaveError")} />
          ) : (
            rootErrorMessage && <Alert variant={"error"} title={rootErrorMessage} />
          )}
          <SalesPersonSettingsFields {...personal} disabled={isSaving} />
        </>
      )}
    </section>
  )
}

/**
 * The coverage target draft, filled from the first loaded company settings so refetches keep it. Its baseline is the
 * loaded or last saved target; only a draft that differs from it is sent.
 */
const useCoverageTargetDraft = () => {
  const {data, error, refetch} = useKlaraCompanySettingsQuery()
  const mutation = useKlaraCompanySettingsMutation()
  const loadedTarget = data?.data.company_settings.coverage_target
  const [baseline, setBaseline] = useState<number | null>(null)
  const [draft, setDraft] = useState("")

  useEffect(() => {
    if (loadedTarget != null && baseline == null) {
      setBaseline(loadedTarget)
      setDraft(String(loadedTarget))
    }
  }, [loadedTarget, baseline])

  // What the draft saves as: clamped to an allowed value, or the baseline while the input holds no number.
  const parsedDraft = Number.parseFloat(draft)
  const target = Number.isNaN(parsedDraft) ? baseline : clampCoverageTarget(parsedDraft)

  const isChanged = target != null && target !== baseline
  const changedTarget = () => (isChanged ? target : null)

  const save = async (nextTarget: number) => {
    const response = await mutation.mutateAsync({coverage_target: nextTarget})
    setBaseline(response.data.company_settings.coverage_target)
  }

  /** Rewrites the input with the value it saves as. */
  const commitDraft = () => {
    if (target != null) {
      setDraft(String(target))
    }
  }

  const step = (direction: 1 | -1) => {
    if (target != null) {
      setDraft(String(clampCoverageTarget(target + direction * COVERAGE_TARGET_STEP)))
    }
  }

  return {error, refetch, draft, target, isChanged, setDraft, commitDraft, step, changedTarget, save}
}

const CompanySettingsBlock: React.FC<{
  company: ReturnType<typeof useCoverageTargetDraft>
  wasSaved: boolean
  error: string | null
}> = ({company, wasSaved, error}) => {
  const {t} = useTranslation()
  const inputId = useId()
  const descriptionId = useId()

  return (
    <section>
      <BlockHeading title={t("KlaraDashboard_Settings_Company_Title")} isSaved={wasSaved && !company.isChanged} />
      {company.target == null ? (
        company.error ? (
          <GenericErrorAlert retry={company.refetch} />
        ) : (
          <Loading size={"xl"} />
        )
      ) : (
        <>
          {error && <Alert variant={"error"} title={error} />}
          <div className={"mb-3 flex items-center justify-between gap-5"}>
            <div>
              <label htmlFor={inputId} className={"text-[15px] font-semibold text-cr-slate-90"}>
                {t("KlaraDashboard_Settings_Company_CoverageTarget_Title")}
              </label>
              <p id={descriptionId} className={"mt-0.5 text-[13px] text-cr-slate-60"}>
                {t("KlaraDashboard_Settings_Company_CoverageTarget_Description")}
              </p>
            </div>
            <div className={"flex shrink-0 items-stretch overflow-hidden rounded-md border border-cr-grey-15"}>
              <button
                type={"button"}
                aria-label={t("KlaraDashboard_Settings_Company_CoverageTarget_Decrease")}
                disabled={company.target <= COVERAGE_TARGET_MIN}
                onClick={() => company.step(-1)}
                className={STEPPER_BUTTON_CLASS_NAME}
              >
                <MinusIcon className={"size-4"} aria-hidden={"true"} />
              </button>
              <div
                data-testid={"coverage target field"}
                // The input itself has no border, so its segment of the stepper shows the focus.
                className={
                  "flex w-[72px] items-baseline justify-center gap-0.5 self-stretch border-x border-cr-slate-15 pt-2.5 focus-within:ring-2 focus-within:ring-cr-blue focus-within:ring-inset"
                }
              >
                <input
                  id={inputId}
                  type={"number"}
                  inputMode={"numeric"}
                  min={COVERAGE_TARGET_MIN}
                  max={COVERAGE_TARGET_MAX}
                  step={COVERAGE_TARGET_STEP}
                  aria-describedby={descriptionId}
                  className={
                    "w-[38px] appearance-none border-none bg-transparent p-0 text-right text-[15px] font-semibold text-cr-slate-90 tabular-nums focus:ring-0 [&::-webkit-inner-spin-button]:appearance-none"
                  }
                  value={company.draft}
                  onChange={event => company.setDraft(event.target.value)}
                  onBlur={company.commitDraft}
                />
                <span className={"text-[15px] text-cr-slate-60"}>%</span>
              </div>
              <button
                type={"button"}
                aria-label={t("KlaraDashboard_Settings_Company_CoverageTarget_Increase")}
                disabled={company.target >= COVERAGE_TARGET_MAX}
                onClick={() => company.step(1)}
                className={STEPPER_BUTTON_CLASS_NAME}
              >
                <PlusIcon className={"size-4"} aria-hidden={"true"} />
              </button>
            </div>
          </div>
          <CurrentCoverage target={company.target} />
        </>
      )}
    </section>
  )
}

/** The selected period's coverage against the drafted target. */
const CurrentCoverage: React.FC<{target: number}> = ({target}) => {
  const {t} = useTranslation()
  const rate = useKlaraSummary().data?.data.coverage.rate
  const percent = rate == null ? null : Math.round(rate * 100)
  const coverage =
    percent == null ? t("KlaraDashboard_Coverage_NoValue") : t("KlaraDashboard_Coverage_Value", {percent})

  return (
    <div className={"flex flex-col gap-1.5 pb-2.5"}>
      <div aria-hidden={"true"} className={"relative mt-1 h-2 w-full rounded-full bg-cr-slate-8"}>
        <div className={"h-full rounded-full bg-cr-blue"} style={{width: `${percent ?? 0}%`}} />
        <div
          className={"absolute -top-1 h-4 w-0.5 -translate-x-1/2 rounded-[1px] bg-cr-blue-dark"}
          style={{left: `${target}%`}}
        />
      </div>
      <p data-testid={"settings current coverage"} className={"text-xs text-cr-slate-60"}>
        {t("KlaraDashboard_Settings_Company_Current", {coverage, target})}
      </p>
    </div>
  )
}
